import classNames from 'classnames'
import { defineMessages, useIntl } from 'react-intl'
import { Text } from '@homebotapp/hb-react-component-catalog'
import { useSelector, useDispatch } from 'react-redux'
import { sendEstimatedPMIRemovalInquiry } from '../../../../store/slices/pmi'
import { selectSponsorshipFirstName } from '../../../../store/selectors/sponsorship'
import { selectCustomerFirstName } from '../../../../store/selectors/customerProfile'
import { OutreachButton } from '../../..'
import { PENDING, SUCCEEDED, FAILED } from '../../../../types/loadingStatus'
import { selectEstimatedPMIInquiryStatus } from '../../../../store/slices/pmi'
import { OUTREACH_BUTTON } from '../../../../constants/messages'
import { Button } from '@homebotapp/hb-react-component-catalog'
import { openLoans } from '../../../../actions/homeowner'
import { PmiStepEnum } from '../PmiEstimatedModule'
import { selectCurrentCELs } from '../../../../store/selectors/home/loans'
import { useTrackingContext } from '../../../../providers/tracking'
import { HOMEOWNER_DIGEST_PMI_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'

import styles from './PmiEstimatedModuleDmStep.module.scss'

export interface PmiEstimatedModuleDmStepProps {
  setStep: (step: PmiStepEnum) => void
}

const MSG = defineMessages({
  successMessageTitle: {
    id: 'Pmi.success.message.title',
    defaultMessage: 'Keep an eye on your inbox'
  },
  successMessageSubtitleCustomer: {
    id: 'Pmi.Estimated.success.message.subtitle.customer',
    defaultMessage: '{customer} will check to see if you have PMI.'
  },
  successMessageSubtitleSponsor: {
    id: 'Pmi.Estimated.success.message.subtitle.sponsor',
    defaultMessage: '{sponsor} is a lender {customer} trusts, and will check to see if you have PMI.'
  },
  askAboutPMI: {
    id: 'PMI.estimated.askAboutPMI',
    defaultMessage: 'Ask if you have PMI'
  },
  nevermind: {
    id: 'PMI.estimated.nevermind',
    defaultMessage: 'Never mind'
  },
  viewCurrentMortgages: {
    id: 'PMI.estimated.view.current.mortgages',
    defaultMessage: 'View your {currentLoans, plural, one {mortgage} other {mortgages}}'
  }
})

export const PmiEstimatedModuleDmStep = ({ setStep }: PmiEstimatedModuleDmStepProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const currentLoans = useSelector(selectCurrentCELs)
  const customerFirstName = useSelector(selectCustomerFirstName)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)
  const requestStatus = useSelector(selectEstimatedPMIInquiryStatus)
  const firstName = sponsorFirstName ? sponsorFirstName : customerFirstName

  const handleDMSubmit = () => {
    dispatch(
      sendEstimatedPMIRemovalInquiry({
        message: intl.formatMessage(CUSTOMER_MESSAGES.MORTGAGE_INSURANCE.tertiaryMessage),
        trackingClient
      })
    )
  }

  const showDMButton = requestStatus === SUCCEEDED || requestStatus === FAILED
  const transformText = requestStatus === SUCCEEDED || requestStatus === FAILED

  const successMessageSubtitle = sponsorFirstName
    ? intl.formatMessage(MSG.successMessageSubtitleSponsor, {
        sponsor: firstName,
        customer: customerFirstName
      })
    : intl.formatMessage(MSG.successMessageSubtitleCustomer, {
        customer: firstName
      })

  return (
    <>
      <div className={styles.btnContainer}>
        <OutreachButton
          className={classNames(styles.outreachButton, transformText && styles.transform)}
          buttonMessage={MSG.askAboutPMI}
          requested={requestStatus === PENDING}
          success={requestStatus === SUCCEEDED}
          failure={requestStatus === FAILED}
          colorScheme='brand'
          resultMessage={
            requestStatus === SUCCEEDED
              ? intl.formatMessage(OUTREACH_BUTTON.success)
              : intl.formatMessage(OUTREACH_BUTTON.failure)
          }
          handleClick={handleDMSubmit}
          tracking={{
            guid: '9z9CQt4YXDGUPT1oCsETEi',
            ui_context: HOMEOWNER_DIGEST_PMI_MODULE,
            descriptor: MSG.askAboutPMI.defaultMessage
          }}
        />
        {!showDMButton && (
          <Button
            type='button'
            onClick={() => setStep(PmiStepEnum.InitialStep)}
            tracking={{
              guid: 'mEky9pH6eC8VceQzGnBao9',
              ui_context: HOMEOWNER_DIGEST_PMI_MODULE,
              descriptor: MSG.nevermind.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.nevermind)}
          </Button>
        )}
      </div>
      {requestStatus === SUCCEEDED && (
        <>
          <div className={styles.margin}>
            <Text as='b' color='neutral-900'>
              {intl.formatMessage(MSG.successMessageTitle)}
            </Text>
          </div>
          <Text marginBottom='4' size='sm'>
            {successMessageSubtitle}
          </Text>
          <Button
            type='button'
            width={['100%', null, 'inherit']}
            onClick={() => dispatch(openLoans())}
            tracking={{
              guid: 'kDmQYVNZSy8Ur17BjR6BuZ',
              ui_context: HOMEOWNER_DIGEST_PMI_MODULE,
              descriptor: 'View your mortgage(s)'
            }}
          >
            {intl.formatMessage(MSG.viewCurrentMortgages, {
              currentLoans: currentLoans.length
            })}
          </Button>
        </>
      )}
    </>
  )
}
